import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';
import { useRouter } from 'next/router';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Link from 'next/link';
import clsx from 'clsx';

import { trackAhoyEvent } from 'utils/ahoy';
import { getRequest, postRequest, putRequest } from 'utils/api';
import { closeModal, openModal } from 'features/utilsSlice';
import { setRedirectTo } from 'utils/localStorage';
import { formatPrice } from 'utils/helpers';
import { updateGigPrice } from 'features/gigsSlice';
import { updateCurrentUser } from 'features/authSlice';
import useCurrentProfile from 'hooks/useCurrentProfile';
import useCurrentUser from 'hooks/useCurrentUser';
import ConfirmationDialog from 'components/ConfirmationDialog';
import LoggedInFooter from 'components/footer/LoggedInFooter';
import CenteredDialog from 'components/CenteredDialog';
import AcceptedGigDialog from './AcceptedGigDialog';
import ApplyGigDialog from './ApplyGigDialog';
import styles from 'styles/components/gigs/GigFooter.module.scss';

const red = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#ff4b4b',
  color: '#fff',
  bottom: 82,
};

const grey = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#ebedf2',
  bottom: 82,
  color: 'rgba(50, 54, 67, 0.6)',
};

const padding = { padding: '10px 0' };

const InfluencerGigFooter = ({ gig, handleClose, fullWidth }) => {
  const [applications, setApplications] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [open, setOpen] = useState({ request: false, acceptedGig: false, confirmation: false });
  const [isLoading, setIsLoading] = useState({ accept: false, reject: false });
  const profile = useCurrentProfile();
  const currentUser = useCurrentUser();
  const router = useRouter();
  const theme = useTheme();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(state => state.auth.loggedIn);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const addApplication = application => {
    setApplications(prevApplications => [...prevApplications, application]);
  };

  const acceptInvitation = () => {
    const invitation = invitations.find(i => i.status === 'pending');

    if (!invitation) {
      alert('Your invitation is no longer valid');
    }

    setIsLoading(prevState => ({ ...prevState, accept: true }));

    postRequest({
      endpoint: `gig_invitations/${invitation.id}/accept`,
    })
      .then(response => {
        dispatch(
          openModal({
            name: 'Accepted Gig',
            data: { invitation: { ...invitation, gig_id: response.data.gig_id } },
          })
        );
        setOpen(prevState => ({ ...prevState, acceptedGig: true }));
        dispatch(closeModal('Confirmation'));
        setIsLoading(prevState => ({ ...prevState, accept: false }));

        trackAhoyEvent('Gig Invitation Acceepted', {
          gig_id: invitation.gig.id,
          gig_invitation_id: invitation.id,
          profile_id: invitation.profile.id,
        });

        handleClose();
      })
      .catch(() => setIsLoading(prevState => ({ ...prevState, accept: false })));
  };

  const rejectInvitation = rejectionReason => {
    const invitation = invitations.find(i => i.status === 'pending');

    if (!invitation) {
      alert('Your invitation is no longer valid');
    }

    setIsLoading(prevState => ({ ...prevState, reject: true }));

    postRequest({
      data: { rejection_reason: rejectionReason },
      endpoint: `gig_invitations/${invitation.id}/reject`,
      successMessage: 'Successfully rejected this gig.',
    })
      .then(() => {
        trackAhoyEvent('Gig Invitation Rejected', {
          gig_id: invitation.gig.id,
          gig_invitation_id: invitation.id,
          profile_id: invitation.profile.id,
        });

        router.push('/dashboard');
        setIsLoading(prevState => ({ ...prevState, reject: false }));
        handleClose();
      })
      .catch(() => setIsLoading(prevState => ({ ...prevState, reject: false })));
  };

  const handleOpenApprove = () => {
    dispatch(openModal({ name: 'Confirmation', data: { action: 'approve' } }));
    setOpen(prevState => ({ ...prevState, confirmation: true }));
  };

  const handleSellerSwitch = () => {
    putRequest({
      endpoint: `users/${currentUser.id}`,
      data: { user: { primary_interest: 'sell' } },
    }).then(() => {
      dispatch(updateCurrentUser({ primary_interest: 'sell' }));
      window.location.reload();
    });
  };

  const getActions = () => {
    let label = {};
    const inv = invitations[0];

    if (!isLoggedIn) return { type: 'redirect', text: 'Login to apply for gig' };

    if (gig.accepted_at && ['active', 'completed'].includes(gig.status))
      return {
        text: (
          <div className={styles.acceptedGigMsg}>
            {inv ? (
              <>
                We apologize. You were invited to {gig.title} by{' '}
                <Link href={`/c/${gig.user.username}`}>{gig.user.display_name}</Link> along with
                other users, however, the advertiser has already hired one of the other invited
                users.
                <br />
                To increase your chances in the future, be sure to respond to offers as soon as
                possible.
              </>
            ) : (
              <>
                We apologize. This gig is no longer active.
                <br />
                Check out other similar gigs on our{' '}
                <span
                  className={styles.link}
                  onClick={() => {
                    handleClose();
                    router.push('/discover');
                  }}
                >
                  discover page
                </span>
                .
              </>
            )}
          </div>
        ),
        labelStyle: red,
      };

    if (invitations.length > 0) {
      if (invitations[0].status === 'pending') {
        return { type: 'accept', text: 'Accept Gig', action: handleOpenApprove };
      } else {
        label = {
          text: 'You have already declined this gig.',
          labelStyle: red,
        };
      }
    }

    if (applications.length > 0) {
      if (applications[0].status === 'pending') {
        let text = 'You already applied to this gig';

        if (applications[0].custom_price) {
          text += ` with an offer of ${formatPrice(applications[0].budget)}.`;
        }

        return {
          text: text,
          labelStyle: grey,
        };
      } else {
        return {
          text: 'Your application has been rejected.',
          labelStyle: red,
        };
      }
    }

    if (currentUser.primary_interest === 'buy') {
      return {
        text:
          'You are currently acting as a buyer, if you want to request this gig switch to your seller profile.',
        labelStyle: grey,
        type: 'buy',
        style: { height: 40 },
      };
    }

    return {
      ...label,
      type: 'request',
      text: 'Request Gig',
      labelStyle: {},
      action: () => {
        dispatch(openModal({ name: 'Apply Gig', data: { gig, profile } }));
        setOpen(prevState => ({ ...prevState, request: true }));
      },
    };
  };

  const goBack = () => router.back();

  const handleDialogClose = () => dispatch(closeModal('Reject Gig'));

  useEffect(() => {
    Promise.all([
      getRequest({ endpoint: 'gig_applications', query: { gig_id: gig.id } }),
      getRequest({ endpoint: 'gig_invitations', query: { gig_id: gig.id } }),
    ])
      .then(([applications, invitations]) => {
        setApplications(applications);
        setInvitations(invitations);

        const invitation = invitations[0];

        if (!invitation) return;

        if (invitation.custom_price) {
          dispatch(
            updateGigPrice({
              id: gig.id,
              amount: invitation.amount,
            })
          );
        }

        if (!invitation.viewed_at) {
          postRequest({ endpoint: `gig_invitations/${invitations[0].id}/view` });
        }

        trackAhoyEvent('Gig Invitation Viewed', {
          gig_id: invitation.gig.id,
          gig_invitation_id: invitation.id,
          profile_id: invitation.profile.id,
        });
      })

      .catch(err => console.log('err', err));
  }, [gig.id, dispatch]);

  const actions = getActions();

  return (
    <div
      className={clsx(styles.footerActions, {
        [styles.moveBottom]: isDesktop,
        [styles.fullWidth]: fullWidth,
      })}
      style={actions.labelStyle}
    >
      {actions.type === 'accept' && (
        <div className={styles.buttons}>
          <Button
            color="primary"
            onClick={() => {
              dispatch(
                openModal({
                  name: 'Confirmation',
                  data: {
                    action: 'reject',
                    rejectTitle: `Reject offer from "${gig.user.display_name}"`,
                  },
                })
              );
              setOpen(prevState => ({ ...prevState, confirmation: true }));
            }}
          >
            Decline
          </Button>

          <Button
            variant="contained"
            color="primary"
            style={actions.style || {}}
            onClick={actions.action}
          >
            {actions.text}
          </Button>
        </div>
      )}

      {actions.type === 'buy' && (
        <div
          className={styles.textWithFooter}
          style={{ backgroundColor: actions.labelStyle.backgroundColor, marginBottom: 15 }}
        >
          <div style={padding}>{actions.text}</div>
          <LoggedInFooter />

          <Button
            variant="contained"
            color="primary"
            style={actions.style || {}}
            onClick={handleSellerSwitch}
          >
            Switch to Seller
          </Button>
        </div>
      )}

      {actions.type === 'redirect' && (
        <>
          <h3
            style={{
              fontSize: 14,
              color: '#5b00db',
              fontFamily: 'Avenir Black',
              letterSpacing: 3.5,
              marginBottom: 15,
              opacity: 0.8,
              textTransform: 'uppercase',
            }}
          >
            Apply to this gig
          </h3>
          <div className={styles.redirectButtons}>
            <Link href="/login" passHref>
              <Button
                color="primary"
                variant="contained"
                onClick={() => setRedirectTo(`/gigs/${gig.id}`)}
              >
                Login
              </Button>
            </Link>

            <Link href="/sign-up" passHref>
              <Button
                color="primary"
                variant="contained"
                onClick={() => setRedirectTo(`/gigs/${gig.id}`)}
              >
                Sign Up
              </Button>
            </Link>
          </div>
        </>
      )}

      {actions.type === 'request' && (
        <div className={styles.buttons}>
          {handleClose ? (
            <Button color="primary" onClick={handleClose}>
              Cancel
            </Button>
          ) : (
            <Button onClick={goBack} color="primary">
              Cancel
            </Button>
          )}

          <Button
            variant="contained"
            color="primary"
            style={actions.style || {}}
            onClick={actions.action}
          >
            {actions.text}
          </Button>
        </div>
      )}

      {!actions.type && (
        <div
          className={styles.textWithFooter}
          style={{ backgroundColor: actions.labelStyle.backgroundColor }}
        >
          <div style={padding}>{actions.text}</div>
          <LoggedInFooter />
        </div>
      )}
      {open.acceptedGig && <AcceptedGigDialog gig={gig} />}
      {open.request && <ApplyGigDialog addApplication={addApplication} handleClose={handleClose} />}
      {open.confirmation && (
        <ConfirmationDialog
          title="Are you sure?"
          handleApprove={acceptInvitation}
          handleReject={() => dispatch(openModal({ name: 'Reject Gig' }))}
          disabled={isLoading.accept || isLoading.reject}
        />
      )}

      <CenteredDialog
        modalName="Reject Gig"
        title={`It's a good idea to leave the "${gig.user.display_name}" a rejection reason message.`}
        placeholder="Message..."
        handleSubmit={rejectInvitation}
        handleDialogClose={handleDialogClose}
        isLoading={isLoading?.reject}
        alwaysEnabled
      />
    </div>
  );
};

InfluencerGigFooter.propTypes = {
  gig: PropTypes.object.isRequired,
  handleClose: PropTypes.func,
};

InfluencerGigFooter.defaultProps = {
  handleClose: null,
};

export default InfluencerGigFooter;
